<template>
    <span data-slot="badge" :class="badgeClass">
        <slot />
    </span>
</template>

<script setup lang="ts">
import type { ClassValue } from 'clsx';
import { computed } from 'vue';
import { cn } from '~/lib/utils';

type BadgeVariant = 'neutral' | 'success' | 'warning' | 'danger' | 'info' | 'accent';
type BadgeSize = 'sm' | 'md';

const props = withDefaults(
    defineProps<{
        variant?: BadgeVariant;
        size?: BadgeSize;
        class?: ClassValue;
    }>(),
    {
        variant: 'neutral',
        size: 'md',
    },
);

const variantClasses: Record<BadgeVariant, string> = {
    neutral: 'bg-primary-500/60 text-primary-200 border-primary-400/20',
    success: 'bg-success-500/15 text-success-300 border-success-500/30',
    warning: 'bg-warning-500/15 text-warning-300 border-warning-500/30',
    danger: 'bg-danger-500/15 text-danger-300 border-danger-500/30',
    info: 'bg-info-500/15 text-info-300 border-info-500/30',
    accent: 'bg-accent-500/15 text-accent-300 border-accent-500/30',
};

const sizeClasses: Record<BadgeSize, string> = {
    sm: 'text-xs px-2 py-0.5',
    md: 'text-xs px-2 py-0.5',
};

const badgeClass = computed(() =>
    cn(
        'inline-flex items-center gap-1 whitespace-nowrap rounded-full border font-medium',
        variantClasses[props.variant],
        sizeClasses[props.size],
        props.class,
    ),
);
</script>
